import React, { useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { PROCESS_VOUCHER_REFUND } from 'store/mutations';
import { GET_BOOKING } from 'store/queries';
import { customerShape } from 'constants/propTypes';
import { useManagedQuery } from 'utils/managedHooks';
import Button from 'components/ui/Button';
import { CurrencyInput } from 'components/ui/Inputs';
import AlertBox from 'components/ui/AlertBox';
import { FullPageSpinner } from 'admin-components/components';
import EmailLanguage from 'components/ui/EmailLanguage';
import StyledReactSelect from 'components/ui/StyledReactSelect';
import TextInput from 'components/ui/TextInput';
import {
  EVOUCHER_DATE_FORMAT,
  EVOUCHER_SERVICE_DATE_FORMAT,
  MAX_VOUCHER_EXPIRY_DATE,
  VOUCHER_LANGUAGES,
  MAP_MARKET_TO_LANGUAGE
} from 'constants/payment';

import { Errors } from '../../SplitPayment/SplitPayment.styles';
import { ErrorMessage, FormWrapper, Spacer } from '../CardRefund/CardRefund.styles';
import {
  mapVoucherCurrencyToMarket,
  voucherExpiryDateInvalid,
  mapVoyagerToEvoucherChannels,
  getPassengersByUniqueEmails
} from '../../Payments.utils';
import { StyledLabel } from '../WriteOffRefund/WriteOffRefund.styles';

import { getVoucherType, buildCustomerData } from './EvoucherRefund.utils';
import { StyledCreateVoucherForm, StyledCreateVoucherFormHeader, StyledChevronRight } from './EvoucherRefund.styles';

const EvoucherRefund = ({ currency, totalPriceToBePaid, customer, bookingReference, loading }) => {
  const [voucherExpiryDate, setVoucherExpiryDate] = useState(MAX_VOUCHER_EXPIRY_DATE);
  const [dateError, setDateError] = useState(false);
  const [voucherEmail, setVoucherEmail] = useState(customer.email || '');
  const [showCreateVoucherForm, setShowCreateVoucherForm] = useState(false);
  const [amountToRefund, setAmount] = useState(totalPriceToBePaid);
  const [voucherLanguage, setVoucherLanguage] = useState(MAP_MARKET_TO_LANGUAGE[mapVoucherCurrencyToMarket[currency]]);
  const createVoucherDisabled = voucherExpiryDateInvalid(voucherExpiryDate) || !VOUCHER_LANGUAGES.includes(voucherLanguage);

  const { data, loading: bookingLoading } = useManagedQuery(GET_BOOKING, {
    variables: { reference: bookingReference, currency }
  });

  const [processVoucherRefund, { loading: processVoucherRefundLoader, error: processVoucherRefundError }] = useMutation(
    PROCESS_VOUCHER_REFUND,
    {
      refetchQueries: ['booking'],
      awaitRefetchQueries: true
    }
  );

  const { register, handleSubmit, clearErrors, formState } = useForm();

  const { errors } = formState;

  const onHandleInputCalendarChange = (date) => {
    setVoucherExpiryDate(date);

    return voucherExpiryDateInvalid(date) ? setDateError(true) : setDateError(false);
  };

  const hasAmountError = Boolean(errors.amount);

  if (bookingLoading) {
    return <FullPageSpinner />;
  }

  const passengers = data.booking.passengers.map((passenger) => ({
    email: passenger.email,
    firstName: passenger.firstName,
    lastName: passenger.lastName
  }));

  const allPassengers = getPassengersByUniqueEmails(customer, passengers);
  const passengerOptions = allPassengers.map(({ email }) => ({ label: email, value: email }));

  const handleCreateVoucher = async () => {
    const passenger = allPassengers.find((pax) => pax.email === voucherEmail);

    const customerData = buildCustomerData(allPassengers, passenger);

    if (amountToRefund > totalPriceToBePaid || amountToRefund <= 0.5) {
      return;
    }

    await processVoucherRefund({
      variables: {
        associatedPNR: bookingReference,
        amount: amountToRefund,
        validUntil: moment(voucherExpiryDate, EVOUCHER_DATE_FORMAT).format(EVOUCHER_SERVICE_DATE_FORMAT).toString(),
        type: getVoucherType(data.booking) ? mapVoyagerToEvoucherChannels(getVoucherType(data.booking)) : 'VTV',
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        recipientEmail: customerData.email,
        bookingReference,
        language: voucherLanguage,
        currency
      }
    });
  };

  const loadingState = processVoucherRefundLoader || bookingLoading || loading;

  return (
    <form data-testid="evoucher-refund-form" data-cy="refund-form" onSubmit={handleSubmit(handleCreateVoucher)}>
      <FormWrapper>
        <header>
          <h2 data-cy="title">
            <FormattedMessage id="payments.AmountToRefundByEvoucher" />
          </h2>
        </header>
        <Spacer>
          <CurrencyInput
            currency={currency}
            dataCy="amount-input"
            dataTestId="amount-input"
            defaultValue={totalPriceToBePaid}
            isControlled={false}
            {...register('amount', {
              required: true,
              min: 1,
              max: totalPriceToBePaid,
              onChange: (e) => {
                clearErrors('amount');
                setAmount(Number(e.target.value));
              }
            })}
            step={0.01}
          />
        </Spacer>

        {hasAmountError && (
          <ErrorMessage data-cy="amount-error-msg">
            <FormattedMessage id="refunds.card.error.amount" values={{ amount: totalPriceToBePaid }} />
          </ErrorMessage>
        )}

        <header>
          <h2 data-cy="title-create">
            <FormattedMessage id="refunds.evoucher.titleCreate" />
          </h2>
        </header>

        <StyledCreateVoucherFormHeader data-cy="generate-eVoucher" onClick={() => setShowCreateVoucherForm(!showCreateVoucherForm)}>
          <h4 data-cy="title-generate">
            <FormattedMessage id="refunds.evoucher.titleGenerateEvoucher" />
          </h4>
          <StyledChevronRight showCreateVoucherForm={showCreateVoucherForm} />
        </StyledCreateVoucherFormHeader>

        {showCreateVoucherForm && (
          <StyledCreateVoucherForm>
            <div>
              <StyledLabel>
                <FormattedMessage id="refunds.evoucher.marketSendVoucher" />
              </StyledLabel>
              <EmailLanguage
                id="voucherLanguage"
                name="voucherLanguage"
                data-cy="create-voucher-language--dropdown"
                placeholder={<FormattedMessage id="payments.dropdownDefault" />}
                onChange={(e) => setVoucherLanguage(e.value)}
                initialValue={voucherLanguage}
                required
                width="300px"
                mt="8px"
                disabled={loading}
                filterOption={({ value }) => VOUCHER_LANGUAGES.includes(value)}
              />
            </div>

            <div>
              <StyledLabel>
                <FormattedMessage id="refunds.evoucher.emailSendVoucher" />
              </StyledLabel>
              <StyledReactSelect
                id="voucherEmail"
                name="voucherEmail"
                data-cy="create-voucher-email--dropdown"
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder={<FormattedMessage id="payments.dropdownDefault" />}
                onChange={(e) => setVoucherEmail(e.value)}
                value={passengerOptions.find((o) => o.value === voucherEmail)}
                options={passengerOptions}
                required
                width="300px"
                mt="8px"
              />
            </div>

            <div>
              <StyledLabel>
                <FormattedMessage id="refunds.evoucher.expiryDateCreate" />
              </StyledLabel>
              <TextInput
                id="voucherExpiry"
                name="voucherExpiry"
                data-cy="create-voucher-expiry"
                type="date"
                pattern="dd/mm/yyyy"
                onChange={(e) => onHandleInputCalendarChange(e.target.value)}
                value={moment(voucherExpiryDate).format(EVOUCHER_DATE_FORMAT)}
                min={moment().format(EVOUCHER_DATE_FORMAT)}
                max={moment(MAX_VOUCHER_EXPIRY_DATE).format(EVOUCHER_DATE_FORMAT)}
                required
                style={{ height: 32, width: 300, marginTop: 8 }}
              />
              {dateError && (
                <Spacer>
                  <AlertBox variant="ERROR">
                    <FormattedMessage id="refunds.evoucher.voucherExpiryDateError" />
                  </AlertBox>
                </Spacer>
              )}
            </div>

            <Button
              data-testid="create"
              data-cy="create"
              type="submit"
              loading={loadingState}
              disabled={createVoucherDisabled || loadingState}
            >
              <FormattedMessage id="refunds.evoucher.createVoucher" />
            </Button>
          </StyledCreateVoucherForm>
        )}

        <Errors>{processVoucherRefundError && <div>{processVoucherRefundError.message}</div>}</Errors>
      </FormWrapper>
    </form>
  );
};

EvoucherRefund.propTypes = {
  currency: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  updatePayments: PropTypes.func,
  customer: customerShape,
  bookingReference: PropTypes.string,
  reference: PropTypes.string,
  method: PropTypes.string,
  loading: PropTypes.bool
};
export default EvoucherRefund;
